/**
 * EntityShipmentsTab - Aba "Remessas" genérica para modais de registros vinculáveis
 * (O.S., Negócio...). Lista via `useShipmentsList` filtrada pelo vínculo genérico
 * (shipment_links), cria com `ShipmentFormDialog` (vínculo + cliente pré-preenchidos)
 * e abre o `ShipmentDetailsModal` oficial.
 *
 * RELEASE: 2026-10-17 v1.2.0 - Lista substituída pela tabela oficial `ShipmentsTable` (mesmas colunas/ordenação da página Remessas).
 * RELEASE: 2026-10-16 v1.1.0 - `createBlockedReason`: oculta "Nova remessa" e mostra o motivo (O.S. fora de Aberta).
 * RELEASE: 2026-10-16 v1.0.0 - Extraído de ServiceOrderShipmentsTab para reaproveitar no Negócio.
 */
import { useState } from 'react';
import { Plus, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useShipmentsList } from '@/hooks/useShipments';
import { ShipmentFormDialog } from '@/components/logistics/ShipmentFormDialog';
import { ShipmentDetailsModal } from '@/components/logistics/ShipmentDetailsModal';
import { ShipmentsTable } from '@/components/logistics/ShipmentsTable';
import type { ShipmentSortField } from '@/components/logistics/shipmentStatus';

export interface EntityShipmentsTabProps {
  /** Tipo do vínculo (allowlist de shipment_links). */
  entityType: 'service_order' | 'deal';
  entityId: string;
  /** Rótulo exibido no vínculo automático da nova remessa. */
  entityLabel?: string | null;
  customer: { id: string; name: string } | null;
  /** Texto do estado vazio. */
  emptyText: string;
  /** Quando informado, a criação fica bloqueada e o motivo é exibido. */
  createBlockedReason?: string | null;
}

export function EntityShipmentsTab({ entityType, entityId, entityLabel, customer, emptyText, createBlockedReason = null }: EntityShipmentsTabProps) {
  const { canCreate, canView, isAdmin } = usePermissionsContext();
  const allowView = isAdmin || canView('log_shipments');
  const allowCreate = isAdmin || canCreate('log_shipments');
  const [formOpen, setFormOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  // Ordenação local, mesmo padrão da página Remessas (allowlist no servidor)
  const [sortField, setSortField] = useState<ShipmentSortField>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const handleSort = (field: ShipmentSortField) => {
    setSortDirection(sortField === field && sortDirection === 'desc' ? 'asc' : 'desc');
    setSortField(field);
  };

  // Lista oficial filtrada pelo vínculo com esta O.S.
  const list = useShipmentsList({
    search: '', status: null, carrierId: null, overdueOnly: false,
    entityType, entityId, page: 1, pageSize: 50, sortField, sortDirection,
  }, allowView);

  if (!allowView) {
    return <p className="p-4 text-sm text-muted-foreground">Sem permissão para visualizar remessas.</p>;
  }

  const rows = list.data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Remessas vinculadas</h3>
        {allowCreate && createBlockedReason && <span className="text-xs text-muted-foreground">{createBlockedReason}</span>}
        {allowCreate && !createBlockedReason && (
          <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setFormOpen(true)}><Plus className="h-3.5 w-3.5" />Nova remessa</Button>
        )}
      </div>

      {!list.isLoading && rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-6 text-sm text-muted-foreground">
          <Truck className="h-6 w-6" />{emptyText}
        </div>
      ) : (
        // Tabela oficial da página Remessas, com rolagem horizontal dentro do modal
        <div className="overflow-x-auto rounded-md border">
          <ShipmentsTable items={rows} isLoading={list.isLoading} sortField={sortField}
            sortDirection={sortDirection} onSort={handleSort} onOpen={setOpenId} />
        </div>
      )}

      <ShipmentFormDialog open={formOpen} onOpenChange={setFormOpen} shipment={null}
        link={{ entity_type: entityType, entity_id: entityId, label: entityLabel }}
        defaultCustomer={customer} onSaved={(id) => setOpenId(id)} />
      <ShipmentDetailsModal shipmentId={openId} open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)} onOpenShipment={setOpenId} />
    </div>
  );
}
